import React, { useState, useEffect, useMemo, useRef } from 'react';
import BarcodeScanner from './BarcodeScanner';
import AddDrinkForm from './AdddrinkForm';
import { caffeineLabel } from './CaffeineFields';
import UnknownDrinkSubmissionForm from './UnknownDrinkSubmissionForm';
import { classifyEanSearchInput, normalizeScannedEan } from '../utils/eanSearch.mjs';
import { beginLatestRequest, claimPendingScan, searchDebounceMs } from '../utils/searchRequestPolicy.mjs';

function normalizeBoolean(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return ['1', 'true', 'yes', 'ja', 'on'].includes(normalized);
  }
  return false;
}

function getName(d) {
  return d.product_name || d.name || d.title || d?.title?.rendered || 'Unbekanntes Getränk';
}
function getEAN(d)        { return d.ean_code || d.ean || d.barcode || d?.acf?.ean || ''; }
function getVolume(d)     { return d.volume ?? d.amount ?? d.size ?? d?.acf?.volume ?? ''; }
function getColor(d)      { return d.color || d.farbe || d.colour || d?.acf?.farbe || ''; }
function getPfandInfo(d)  { return d.pfand_info || d.deposit_info || d?.acf?.pfand_info || ''; }
function getAlcohol(d)    { return d.alcohol_content ?? d.alc ?? d?.acf?.alcohol_content ?? ''; }
function getLinks(d)      { return { external: d.external_url || '', internal: d.internal_url || '' }; }
function getPackageVariants(d) { return Array.isArray(d?.package_variants) ? d.package_variants : []; }

const ACTIVITY_SCAN_API_URL = window?.pfandpiratData?.activityScanApiUrl || '/wp-json/pfandpirat/v1/activity/barcode-scan';
const UNRESOLVED_SCAN_API_URL = window?.pfandpiratData?.unresolvedDrinkScanApiUrl || '/wp-json/pfandpirat/v1/drinks/unresolved-scan';
const DRINK_SUBMISSION_API_URL = window?.pfandpiratData?.drinkSubmissionApiUrl || '/wp-json/pfandpirat/v1/drink-submissions';
const TOP_DRINKS_RADIUS_KM = 25;

function resolveTopDrinksPosition() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }

    let settled = false;
    let timeoutId = null;
    const finish = (position) => {
      if (settled) return;
      settled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
      resolve(position);
    };

    timeoutId = window.setTimeout(() => finish(null), 3000);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = Number(pos?.coords?.latitude);
        const longitude = Number(pos?.coords?.longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          finish(null);
          return;
        }

        finish({
          latitude: Number(latitude.toFixed(6)),
          longitude: Number(longitude.toFixed(6)),
        });
      },
      () => finish(null),
      {
        enableHighAccuracy: false,
        timeout: 2500,
        maximumAge: 5 * 60 * 1000,
      }
    );
  });
}

function formatVolume(v) {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  const lower = s.toLowerCase();
  if (lower.endsWith('ml') || lower.endsWith('cl') || lower.endsWith('l')) return s;
  return s + ' L';
}

function formatAlcohol(a) {
  if (a === null || a === undefined) return '';
  const s = String(a).trim();
  if (s.includes('%')) return s;
  return s + ' %';
}

function formatEuro(amount) {
  const n = Number(amount || 0);
  return `${n.toFixed(2).replace('.', ',')} €`;
}

function parsePfandAmount(pfand) {
  const m = (pfand || '').match(/[\d,.]+/);
  return m ? parseFloat(m[0].replace(',', '.')) : 0;
}

function inferPackageUnits(d) {
  const explicit = Number(d?.units_per_package ?? d?.package_units ?? 0);
  if (Number.isFinite(explicit) && explicit > 1) {
    return Math.round(explicit);
  }

  const text = [getName(d), d?.package_label, d?.description]
    .map((value) => String(value || '').toLowerCase())
    .join(' ');

  const multiplyMatch = text.match(/\b([2-9]\d?)\s*x\s*\d+(?:[,.]\d+)?\s*(?:l|liter|ml)?\b/);
  if (multiplyMatch) {
    return Math.max(1, Number.parseInt(multiplyMatch[1], 10));
  }

  const packageMatch = text.match(/\b([2-9]\d?)\s*(?:er|flaschen|flasche|gebinde|pack|tray|kasten)\b/);
  if (packageMatch) {
    return Math.max(1, Number.parseInt(packageMatch[1], 10));
  }

  return 1;
}

function buildPackageOptions(d, singleAmount) {
  const inferredUnits = inferPackageUnits(d);
  const options = [{
    id: 'single',
    package_type: inferredUnits > 1 ? 'case' : 'single',
    label: inferredUnits > 1 ? `${inferredUnits}er Gebinde` : 'Einzeln',
    units_per_package: inferredUnits,
    total_deposit_amount: singleAmount,
    total_deposit_display: formatEuro(singleAmount),
    confidence: 'confirmed',
  }];

  getPackageVariants(d).forEach((variant, index) => {
    const amount = Number(variant.total_deposit_amount ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) return;
    options.push({
      ...variant,
      id: variant.id || variant.key || `package-${index}`,
      label: variant.label || `${variant.units_per_package || ''}er Gebinde`.trim(),
      package_type: variant.package_type || 'case',
      total_deposit_amount: amount,
      total_deposit_display: variant.total_deposit_display || formatEuro(amount),
    });
  });

  return options;
}

function useDrinkImage(d) {
  const staticUrl =
    d.image_url ||
    d.thumbnail ||
    d.featured_media_url ||
    d.media_url ||
    d.image ||
    d?._embedded?.['wp:featuredmedia']?.[0]?.media_details?.sizes?.medium?.source_url ||
    d?._embedded?.['wp:featuredmedia']?.[0]?.source_url ||
    null;

  const [url, setUrl] = useState(staticUrl);

  useEffect(() => {
    let active = true;
    setUrl(staticUrl || null);

    async function loadByMediaId(id) {
      try {
        const res = await fetch(`/wp-json/wp/v2/media/${id}`, { credentials: 'same-origin' });
        if (!res.ok) return;
        const media = await res.json();
        const best =
          media?.media_details?.sizes?.medium?.source_url ||
          media?.media_details?.sizes?.thumbnail?.source_url ||
          media?.source_url;
        if (active) setUrl(best || null);
      } catch {}
    }

    if (!staticUrl && d.image_id) {
      loadByMediaId(d.image_id);
    }

    return () => {
      active = false;
    };
  }, [staticUrl, d.image_id]);

  return url;
}

function Row({ k, v }) {
  return (
    <div className="drink-card__row" style={styles.row}>
      <dt className="drink-card__dt" style={styles.dt}>{k}</dt>
      <dd className="drink-card__dd" style={styles.dd}>{String(v)}</dd>
    </div>
  );
}

function DrinkCard({ d, onAdd, activitySource = 'search' }) {
  const [pending, setPending] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedPackageId, setSelectedPackageId] = useState(() => {
    const matched = d?.matched_package_variant;
    return matched?.id || matched?.key || 'single';
  });
  const usagePercent = typeof d.usage_percent === 'number'
    ? `${d.usage_percent.toFixed(1)} %`
    : null;

  const name   = getName(d);
  const ean    = getEAN(d);
  const volume = getVolume(d);
  const color  = getColor(d);
  const pfand  = getPfandInfo(d);
  const alc    = getAlcohol(d);
  const { external, internal } = getLinks(d);
  const image = useDrinkImage(d);

  const pfandAmount = useMemo(() => parsePfandAmount(pfand), [pfand]);
  const packageOptions = useMemo(() => buildPackageOptions(d, pfandAmount), [d, pfandAmount]);
  const selectedPackage = useMemo(
    () => packageOptions.find((option) => String(option.id) === String(selectedPackageId)) || packageOptions[0],
    [packageOptions, selectedPackageId]
  );
  const selectedUnitAmount = Number(selectedPackage?.total_deposit_amount || 0);
  const totalAmount = Number((selectedUnitAmount * quantity).toFixed(2));
  const packageLabel = selectedPackage?.package_type === 'single'
    ? 'Einzelflasche'
    : selectedPackage?.label || 'Gebinde';
  const packageSuffix = quantity > 1 ? `${quantity} x ${packageLabel}` : packageLabel;

  const payload = {
    type: 'drink',
    product_name: selectedPackage?.package_type === 'single' ? name : `${name} (${packageLabel})`,
    ean_code: selectedPackage?.ean_code || ean,
    volume,
    color,
    alcohol_content: alc,
    pfand_info: selectedPackage?.total_deposit_display || pfand,
    quantity,
    package_type: selectedPackage?.package_type || 'single',
    package_label: packageLabel,
    package_units: selectedPackage?.units_per_package || 1,
    unit_amount: selectedUnitAmount,
    package_deposit: selectedUnitAmount,
    description: packageSuffix,
    image_url: image || '',
    external_url: external || '',
    internal_url: internal || '',
    source: activitySource || 'search',
    source_id: d.source_id || d.id || null
  };

  return (
    <li className="drink-card" style={styles.card}>
      <div className="drink-card__media" style={styles.media}>
        {image ? (
          <img src={image} alt={name} style={styles.img} loading="lazy" />
        ) : (
          <div className="drink-card__placeholder" style={styles.placeholder}>Kein Bild</div>
        )}
      </div>

      <div className="drink-card__body" style={styles.body}>
        <div className="drink-card__header" style={styles.header}>
          <h3 className="drink-card__title" style={styles.title}>{name}</h3>
        </div>

        <dl className="drink-card__meta" style={styles.meta}>
          {usagePercent && <Row k="Anteil" v={usagePercent} />}
          {volume !== '' && <Row k="Menge" v={formatVolume(volume)} />}
          {color !== '' && <Row k="Farbe" v={color} />}
          {pfand !== '' && <Row k="Pfand" v={selectedPackage?.total_deposit_display || pfand} />}
          {alc !== '' && <Row k="Alkohol" v={formatAlcohol(alc)} />}
          {Object.prototype.hasOwnProperty.call(d, 'caffeine_status') && <Row k="Koffein" v={caffeineLabel(d)} />}
          {ean !== '' && <Row k="EAN" v={ean} />}
        </dl>

        <div className="drink-card__links" style={styles.links}>
          {internal ? (
            <a href={internal} target="_blank" rel="noopener noreferrer">Mehr erfahren</a>
          ) : null}
        </div>

        {packageOptions.length > 1 && (
          <div className="drink-card__packages" style={styles.packagePanel}>
            <div style={styles.packageOptions}>
              {packageOptions.map((option) => {
                const isActive = String(option.id) === String(selectedPackage?.id);
                const isSuggested = option.confidence === 'suggested';
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelectedPackageId(option.id)}
                    title={isSuggested ? 'Aus Pfandmatrix vorgeschlagen, bitte bei Bedarf pruefen.' : option.label}
                    style={{
                      ...styles.packageOption,
                      ...(isActive ? styles.packageOptionActive : null),
                    }}
                  >
                    <strong>{option.label}</strong>
                    <span>{option.total_deposit_display}</span>
                    {isSuggested ? <small>Vorschlag</small> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="drink-card__actions" style={styles.actions}>
          <div className="drink-card__quantity" style={styles.quantity}>
            <button
              type="button"
              onClick={() => setQuantity((current) => Math.max(1, current - 1))}
              disabled={pending || quantity <= 1}
              aria-label="Menge verringern"
              style={styles.quantityButton}
            >
              -
            </button>
            <span style={styles.quantityValue}>{quantity}</span>
            <button
              type="button"
              onClick={() => setQuantity((current) => Math.min(99, current + 1))}
              disabled={pending}
              aria-label="Menge erhoehen"
              style={styles.quantityButton}
            >
              +
            </button>
          </div>

          <button
            className="pfand-button"
            disabled={pending}
            onClick={async () => {
              if (pending) return;
              setPending(true);
              try {
                const userId = window?.pfandpiratData?.user?.id ?? null;
                await Promise.resolve(onAdd(totalAmount, payload, userId));
              } finally {
                setPending(false);
              }
            }}
          >
            {pending ? '…' : `${formatEuro(totalAmount)} hinzufügen`}
          </button>

          {external && (
            <button
              type="button"
              className="pfand-button pfand-button--order"
              onClick={() => window.open(external, '_blank', 'noopener,noreferrer')}
            >
              Jetzt bestellen!
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function DrinkList({ data, onAdd, activitySource = 'search' }) {
  return (
    <ul className="drink-list" style={styles.list}>
      {data.map((d) => (
        <DrinkCard
          key={d.id || getEAN(d) || getName(d)}
          d={d}
          onAdd={onAdd}
          activitySource={activitySource}
        />
      ))}
    </ul>
  );
}

export default function SearchView({
  apiUrl = '/wp-json/pfandpirat/v1/drinks',
  createUrl = '/wp-json/pfandpirat/v1/drinks',
  onAdd = () => {},
  autoOpenScanner = false,
  topApiUrl = '/wp-json/pfandpirat/v1/drinks/top?limit=10',
  barcode = '',
  canManageDrinks: canManageDrinksProp = null,
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showScanner, setShowScanner] = useState(autoOpenScanner);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showSubmissionForm, setShowSubmissionForm] = useState(false);
  const [submissionFeedback, setSubmissionFeedback] = useState(null);
  const [scanFeedback, setScanFeedback] = useState('');
  const [unknownInfo, setUnknownInfo] = useState(null);
  const [inputOrigin, setInputOrigin] = useState('search');
  const [topItems, setTopItems] = useState([]);
  const [topLoading, setTopLoading] = useState(false);
  const [topMeta, setTopMeta] = useState({
    label: 'Beliebte Getränke',
    scope: 'global',
    fallback: false,
  });
  const searchInput = useMemo(() => classifyEanSearchInput(term), [term]);
  const runtimeCanManageDrinks = useMemo(() => normalizeBoolean(window?.pfandpiratData?.canManageDrinks), []);
  const canManageDrinks = useMemo(() => {
    if (canManageDrinksProp !== null && canManageDrinksProp !== undefined) {
      return normalizeBoolean(canManageDrinksProp);
    }

    return runtimeCanManageDrinks;
  }, [canManageDrinksProp, runtimeCanManageDrinks]);
  const lastScanRef = useRef('');
  const pendingScanRef = useRef('');
  const lastUnknownTrackedRef = useRef('');
  const searchSequenceRef = useRef(0);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    if (showScanner || term.trim()) {
      setTopLoading(false);
      return () => controller.abort();
    }

    (async function loadTop() {
      try {
        setTopLoading(true);

        const origin = window.location.origin;
        const u = new URL(topApiUrl, origin);
        const position = await resolveTopDrinksPosition();
        if (!active) return;

        if (position) {
          u.searchParams.set('lat', String(position.latitude));
          u.searchParams.set('lng', String(position.longitude));
          u.searchParams.set('radius_km', String(TOP_DRINKS_RADIUS_KM));
        }

        const res = await fetch(u.href, {
          method: 'GET',
          credentials: 'omit',
          cache: 'default',
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`Top-API fehlgeschlagen: ${res.status}`);

        const json = await res.json();
        const items = Array.isArray(json) ? json : [];
        const first = items[0] || {};
        if (active) {
          setTopItems(items);
          setTopMeta({
            label: first.ranking_label || (position ? `Beliebt in deiner Nähe (${TOP_DRINKS_RADIUS_KM} km)` : 'Beliebte Getränke'),
            scope: first.ranking_scope || (position ? 'local' : 'global'),
            fallback: Boolean(first.ranking_fallback),
          });
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (active) {
          setTopItems([]);
          setTopMeta({
            label: 'Beliebte Getränke',
            scope: 'global',
            fallback: false,
          });
        }
      } finally {
        if (active) setTopLoading(false);
      }
    })();

    return () => {
      active = false;
      controller.abort();
    };
  }, [showScanner, term, topApiUrl]);

  useEffect(() => {
    if (autoOpenScanner) {
      setShowScanner(true);
    }
  }, [autoOpenScanner]);

  useEffect(() => {
    const nextBarcode = String(barcode || '').trim();
    if (!nextBarcode) return;
    handleScanResult(nextBarcode);
  }, [barcode]);

  const resetUnknownState = () => {
    setShowAddForm(false);
    setShowSubmissionForm(false);
    setUnknownInfo(null);
  };

  const trackUnknownBarcode = async (code, signal) => {
    const normalized = String(code || '').trim();
    if (!/^\d{8,14}$/.test(normalized)) return null;
    if (lastUnknownTrackedRef.current === normalized) return null;

    lastUnknownTrackedRef.current = normalized;

    try {
      const res = await fetch(UNRESOLVED_SCAN_API_URL, {
        method: 'POST',
        credentials: 'include',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
        },
        body: JSON.stringify({
          barcode: normalized,
          source: inputOrigin === 'scan' ? 'scan' : 'search',
        }),
      });

      if (!res.ok) return null;
      const payload = await res.json();
      return payload && typeof payload === 'object'
        ? {
            ...payload,
            can_manage_drinks: normalizeBoolean(payload.can_manage_drinks),
          }
        : null;
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      console.warn('Unbekannter Barcode konnte nicht protokolliert werden.', error);
      return null;
    }
  };

  const trackScanActivity = async (barcodeValue, match = null) => {
    try {
      const activityResponse = await fetch(ACTIVITY_SCAN_API_URL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': window?.pfandpiratData?.nonce || '',
        },
        body: JSON.stringify({
          barcode: barcodeValue,
          product_name: match ? getName(match) : '',
          source_id: match?.source_id || match?.id || 0,
          internal_url: match?.internal_url || '',
          external_url: match?.external_url || '',
        }),
      });

      if (activityResponse.ok && match) {
        setScanFeedback('Getränk erkannt. Füge den Fund hinzu, um es in deinem Getränke-Logbuch zu entdecken.');
      }
    } catch (error) {
      console.warn('Scan-Aktivität konnte nicht gespeichert werden.', error);
    }
  };

  const handleScanResult = (code) => {
    const normalizedCode = normalizeScannedEan(code);
    if (!normalizedCode) return;
    if (lastScanRef.current === normalizedCode) return;

    lastScanRef.current = normalizedCode;
    pendingScanRef.current = normalizedCode;
    setInputOrigin('scan');

    setTimeout(() => {
      if (lastScanRef.current === normalizedCode) {
        lastScanRef.current = '';
      }
    }, 1500);

    setTerm(normalizedCode);
    setScanFeedback('');
    setShowScanner(false);
  };

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const isLatestRequest = beginLatestRequest(searchSequenceRef);

    async function runSearch() {
      const q = searchInput.normalized;

      if (searchInput.kind === 'empty' || (searchInput.kind === 'text' && q.length < 2)) {
        setResults([]);
        setLoading(false);
        setSearchError('');
        resetUnknownState();
        return;
      }

      if (!searchInput.valid) {
        setResults([]);
        setLoading(false);
        setSearchError('');
        resetUnknownState();
        return;
      }

      const isCode = searchInput.kind === 'ean';
      setLoading(true);
      setSearchError('');

      const origin = window.location.origin;
      const searchUrl = new URL(apiUrl, origin);
      searchUrl.searchParams.set('search', q);

      try {
        const response = await fetch(searchUrl.href, {
          credentials: 'omit',
          cache: 'default',
          signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Getränke-API fehlgeschlagen: ${response.status}`);
        const arr = await response.json();
        const list = Array.isArray(arr) ? arr : (arr?.items || []);

        if (!active || !isLatestRequest()) return;

        setResults(list);

        if (isCode && inputOrigin === 'scan' && claimPendingScan(pendingScanRef, q)) {
          const exactMatches = list.filter(item => normalizeScannedEan(getEAN(item)) === q);
          void trackScanActivity(q, exactMatches.length === 1 ? exactMatches[0] : null);
        }

        if (isCode && list.length === 0) {
          const tracked = await trackUnknownBarcode(q, controller.signal);
          if (!active || !isLatestRequest()) return;

          const serverCanManageDrinks = normalizeBoolean(tracked?.can_manage_drinks);
          const shouldShowAddForm = serverCanManageDrinks || canManageDrinks;

          setUnknownInfo(tracked);
          setShowAddForm(shouldShowAddForm);
          setShowSubmissionForm(!shouldShowAddForm);
        } else {
          resetUnknownState();
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        if (!active || !isLatestRequest()) return;
        setResults([]);
        setSearchError('Die Getränkesuche ist gerade nicht erreichbar. Bitte versuche es erneut.');
        resetUnknownState();
      } finally {
        if (active && isLatestRequest()) setLoading(false);
      }
    }

    const searchTimer = window.setTimeout(runSearch, searchDebounceMs(searchInput.kind));

    return () => {
      active = false;
      window.clearTimeout(searchTimer);
      controller.abort();
    };
  }, [searchInput.kind, searchInput.normalized, searchInput.valid, apiUrl, inputOrigin]);

  return (
    <div className="pfp-pirate-page pfp-search-page">
      <h2>Getränke suchen</h2>

      <div className="pfp-search-toolbar" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <input
          className="pfp-search-input"
          type="search"
          placeholder="Produktname oder EAN‑Code"
          value={term}
          onChange={(e) => {
            setInputOrigin('search');
            setSubmissionFeedback(null);
            lastUnknownTrackedRef.current = '';
            setTerm(e.target.value);
          }}
          style={{ flex: 1, padding: '0.5rem', fontSize: '1rem' }}
        />

        <button
          type="button"
          className={`pfp-search-scan-button ${showScanner ? 'is-active' : ''}`}
          onClick={() => setShowScanner(!showScanner)}
          title="Barcode scannen"
          aria-label={showScanner ? 'Kamera-Scanner schließen' : 'EAN-Code mit der Kamera scannen'}
          style={{
            padding: '0.5rem',
            fontSize: '1.3rem',
            cursor: 'pointer',
            background: showScanner ? '#4CAF50' : '#e9e9e9',
            border: '1px solid #ccc',
            borderRadius: 4,
          }}
        >
          {showScanner ? '✖' : '📷'}
        </button>
      </div>

      {showScanner && (
        <div className="pfp-search-scanner" style={{ marginBottom: 20 }}>
          <BarcodeScanner
            onDetected={handleScanResult}
            onClose={() => setShowScanner(false)}
          />
        </div>
      )}

      {loading && <p>Lädt…</p>}
      {!searchInput.valid && <p className="pfp-search-validation" role="alert">{searchInput.message}</p>}
      {searchError && <p className="pfp-search-error" role="alert">{searchError}</p>}
      {scanFeedback && <p className="pfp-search-feedback" role="status">{scanFeedback}</p>}
      {submissionFeedback && (
        <div className={`unknown-drink-feedback ${submissionFeedback.type || 'info'}`}>
          {submissionFeedback.message}
        </div>
      )}

      {unknownInfo?.pending_submission_id && !canManageDrinks && (
        <div className="unknown-drink-feedback info">
          Zu diesem Barcode gibt es bereits einen offenen Vorschlag. Du kannst ihn unten ergänzen oder einfach später nochmal prüfen.
        </div>
      )}


      {term.trim().length < 2 && results.length === 0 && (
        <section className="pfp-search-popular" style={{ marginTop: 8 }}>
          <h3 style={{ margin: '12px 0 8px' }}>{topMeta.label || 'Beliebte Getränke'}</h3>
          {topLoading ? (
            <p>Lädt…</p>
          ) : topItems.length > 0 ? (
            <DrinkList
              data={topItems}
              onAdd={onAdd}
              activitySource="search"
            />
          ) : (
            <p>Keine Vorschläge verfügbar.</p>
          )}
        </section>
      )}

      {results.length > 0 ? (
        <DrinkList
          data={results}
          onAdd={onAdd}
          activitySource={inputOrigin === 'scan' ? 'scan' : 'search'}
        />
      ) : (
        !loading &&
        searchInput.kind === 'ean' &&
        !showAddForm && <p>Kein Treffer für diesen Code.</p>
      )}

      {showAddForm && (
        <AddDrinkForm
          defaultEan={term.trim()}
          apiUrl={createUrl}
          onCancel={() => setShowAddForm(false)}
          onSuccess={(obj) => {
            resetUnknownState();
            setSubmissionFeedback(null);
            setResults([obj]);
          }}
        />
      )}

      {showSubmissionForm && (
        <UnknownDrinkSubmissionForm
          defaultEan={term.trim()}
          apiUrl={DRINK_SUBMISSION_API_URL}
          onCancel={() => setShowSubmissionForm(false)}
          onSuccess={(payload) => {
            if (payload?.mode === 'already_exists' && payload?.drink) {
              resetUnknownState();
              setSubmissionFeedback({ type: 'success', message: 'Dieses Getränk ist inzwischen schon angelegt und wird jetzt angezeigt.' });
              setResults([payload.drink]);
              return;
            }

            if (payload?.mode === 'already_pending') {
              setSubmissionFeedback({ type: 'info', message: 'Zu diesem Barcode gibt es bereits einen offenen Vorschlag. Ein Admin prüft ihn noch.' });
            } else {
              setSubmissionFeedback({ type: 'success', message: 'Vorschlag gespeichert. Ein Admin prüft das Getränk, bevor es in der Suche erscheint.' });
            }

            setShowSubmissionForm(false);
          }}
        />
      )}
    </div>
  );
}

const styles = {
  list: { listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 },
  card: {
    background: '#fff',
    border: '1px solid #e7e7e7',
    borderRadius: 12,
    boxShadow: '0 2px 8px #0001',
    display: 'grid',
    gridTemplateColumns: '110px 1fr',
    gap: 12,
    padding: 12,
    alignItems: 'center'
  },
  media: {
    width: 110,
    height: 110,
    borderRadius: 8,
    overflow: 'hidden',
    display: 'grid',
    placeItems: 'center',
    background: '#fafafa'
  },
  img: { width: '100%', height: '100%', objectFit: 'cover' },
  placeholder: { fontSize: '.9rem', color: '#888' },
  body: { minWidth: 0 },
  header: { display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8 },
  title: { margin: 0, fontSize: '1.05rem', lineHeight: 1.3 },
  meta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 8,
    margin: '0 0 10px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 6,
    margin: 0,
    padding: '6px 8px',
    border: '1px solid #efefef',
    borderRadius: 8,
    background: '#fcfcfc'
  },
  dt: { margin: 0, color: '#666', fontWeight: 600 },
  dd: { margin: 0, color: '#111', minWidth: 0, overflowWrap: 'anywhere' },
  links: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  packagePanel: { margin: '0 0 10px' },
  packageOptions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  packageOption: {
    minWidth: 132,
    border: '1px solid #d8d8d8',
    borderRadius: 8,
    background: '#fff',
    padding: '8px 10px',
    display: 'grid',
    gap: 2,
    textAlign: 'left',
    cursor: 'pointer',
  },
  packageOptionActive: {
    borderColor: '#28a745',
    boxShadow: '0 0 0 2px rgba(40, 167, 69, 0.18)',
  },
  actions: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  quantity: {
    display: 'inline-grid',
    gridTemplateColumns: '36px 38px 36px',
    alignItems: 'center',
    border: '1px solid #d8d8d8',
    borderRadius: 8,
    overflow: 'hidden',
    background: '#fff',
  },
  quantityButton: {
    width: 36,
    height: 36,
    border: 0,
    background: '#f2f2f2',
    cursor: 'pointer',
    fontWeight: 800,
    fontSize: 18,
  },
  quantityValue: {
    minWidth: 38,
    textAlign: 'center',
    fontWeight: 800,
  },
};
